import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useUserRoleFromUrl } from 'shared/hooks';
import { businessStore } from 'entities/index';
import { MiniInput, DefaultButton, TimeRangePicker } from 'shared/ui';
import { ModeSwitcher } from './mode-switcher';
import { WeeklyForm } from './weekly-form';
import { ShiftForm } from './shift-form';
import { DailyForm } from './daily-form';
import { PeriodsList } from './periods-list';
import { RulesList } from './rules-list';
import { MiniScheduleCalendar } from './mini-schedule-calendar';
import { useWorkSchedule } from '../model/use-work-schedule';
import { WEEK_DAYS_ENG } from '../interfaces/schedule-period.interface';
import type {
  IWeeklyConfig,
  IShiftConfig,
} from '../interfaces/schedule-period.interface';
import styles from './work-schedule.module.scss';

export const WorkSchedule: React.FC = () => {
  const { id } = useParams();
  const role = useUserRoleFromUrl();
  const pointId = businessStore.pointId;

  const {
    periods,
    rawWorkDays,
    rawWorkBreaks,
    mode,
    dateStart,
    dateEnd,
    formConfig,
    isLoading,
    isSaving,
    error,
    refreshKey,
    setMode,
    setDateStart,
    setDateEnd,
    setFormConfig,
    addPeriod,
    deletePeriod,
    deleteWorkDay,
    deleteWorkBreak,
  } = useWorkSchedule({ userId: id, pointId, role });

  const [applyStart, setApplyStart] = useState<string | null>('09:00');
  const [applyEnd, setApplyEnd] = useState<string | null>('18:00');

  const handleApplyToAll = () => {
    if (applyStart === null || applyEnd === null) return;

    if (mode === 'weekly' && formConfig && 'monday' in formConfig) {
      const weekly = formConfig as IWeeklyConfig;
      const updated = { ...weekly };
      for (const dayKey of WEEK_DAYS_ENG) {
        const day = updated[dayKey];
        if (day && !day.isDayOff) {
          updated[dayKey] = {
            ...day,
            startedAt: applyStart,
            finishedAt: applyEnd,
          };
        }
      }
      setFormConfig(updated);
    } else if (mode === 'shift' && formConfig && 'daysRunning' in formConfig) {
      const shift = formConfig as IShiftConfig;
      setFormConfig({
        ...shift,
        workingDays: shift.workingDays.map((day) =>
          day.isDayOff
            ? day
            : { ...day, startedAt: applyStart, finishedAt: applyEnd }
        ),
      });
    }
  };

  return (
    <div className={styles.work_schedule}>
      <div className={styles.work_schedule__content}>
        <div className={styles.content__title}>График работы</div>

        <ModeSwitcher value={mode} onChange={setMode} />

        <div className={styles.content__dates}>
          <div className={styles.content__dates_label}>С</div>
          <div className={styles.dates_input_wrapper}>
            <MiniInput
              inputValue={dateStart}
              onChange={setDateStart}
              inputType="date"
              className={styles.input_date}
            />
          </div>
          <div className={styles.content__dates_label}>До</div>
          <div className={styles.dates_input_wrapper}>
            <MiniInput
              inputValue={dateEnd}
              onChange={setDateEnd}
              inputType="date"
              className={styles.input_date}
            />
          </div>
        </div>

        {(mode === 'weekly' || mode === 'shift') && (
          <div className={styles.content__apply_all}>
            <TimeRangePicker
              index={0}
              timeRange={{ start: applyStart, end: applyEnd }}
              onChange={(data) => {
                setApplyStart(data.timeRange.start);
                setApplyEnd(data.timeRange.end);
              }}
            />
            <DefaultButton
              buttonText="Применить ко всем дням"
              color="white"
              onClick={handleApplyToAll}
              className={styles.btn_apply_all}
            />
          </div>
        )}

        <div className={styles.content__form}>
          {isLoading ? (
            <div className={styles.form_loading}>Загрузка...</div>
          ) : (
            <>
              {mode === 'weekly' && formConfig && 'monday' in formConfig && (
                <WeeklyForm value={formConfig} onChange={setFormConfig} />
              )}
              {mode === 'shift' &&
                formConfig &&
                'daysRunning' in formConfig && (
                  <ShiftForm value={formConfig} onChange={setFormConfig} />
                )}
              {mode === 'daily' &&
                formConfig &&
                'startedAt' in formConfig &&
                !('daysRunning' in formConfig) &&
                !('monday' in formConfig) && (
                  <DailyForm value={formConfig} onChange={setFormConfig} />
                )}
            </>
          )}
        </div>

        <div className={styles.content__actions}>
          <DefaultButton
            buttonText={isSaving ? 'Сохранение...' : 'Сохранить рассписание'}
            color="green"
            onClick={addPeriod}
            disabled={isSaving}
            className={styles.btn_add}
          />
        </div>

        {error && (
          <div className={styles.content__error}>
            <span className={styles.error__text}>{error}</span>
          </div>
        )}

        <MiniScheduleCalendar
          pointId={pointId}
          employeeId={id}
          refreshKey={refreshKey}
          currentDate={
            periods.length > 0
              ? periods.reduce(
                  (min, p) => (p.dateStart < min ? p.dateStart : min),
                  periods[0].dateStart
                )
              : undefined
          }
        />

        <PeriodsList periods={periods} onDelete={deletePeriod} />

        <RulesList
          workDays={rawWorkDays}
          workBreaks={rawWorkBreaks}
          onDeleteDay={deleteWorkDay}
          onDeleteBreak={deleteWorkBreak}
        />
      </div>
    </div>
  );
};
